import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, FormControl, InputLabel, Select,
  MenuItem, TextField, TablePagination, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Grid
} from '@mui/material';
import { Refresh as RefreshIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { logsAPI } from '../services/api';

const AdminLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [filters, setFilters] = useState({
    user_id: '',
    entity_type: '',
    action: ''
  });
  const [availableFilters, setAvailableFilters] = useState({
    entity_types: [],
    actions: [],
    users: []
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  useEffect(() => {
    loadFilters();
  }, []);

  useEffect(() => {
    loadLogs();
    loadCount();
  }, [page, rowsPerPage, filters]);

  const loadFilters = async () => {
    try {
      const response = await logsAPI.getFilters();
      setAvailableFilters(response.data);
    } catch (error) {
      console.error('Error loading filters:', error);
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== '')
        )
      };
      const response = await logsAPI.getAll(params);
      setLogs(response.data);
    } catch (error) {
      console.error('Error loading logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCount = async () => {
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== '')
      );
      const response = await logsAPI.getCount(params);
      setTotalCount(response.data.count);
    } catch (error) {
      console.error('Error loading count:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setDetailsDialogOpen(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ru-RU');
  };

  const getActionColor = (action) => {
    const colors = {
      create: 'success',
      update: 'info',
      delete: 'error',
      login: 'default'
    };
    return colors[action] || 'default';
  };

  const getActionLabel = (action) => {
    const labels = {
      create: 'Создание',
      update: 'Изменение',
      delete: 'Удаление',
      login: 'Вход'
    };
    return labels[action] || action;
  };

  const getEntityTypeLabel = (entityType) => {
    const labels = {
      channel: 'ТВ канал',
      playlist: 'Плейлист',
      ad_block: 'Рекламный блок',
      application: 'Приложение',
      radio_channel: 'Радио канал',
      radio_playlist: 'Радио плейлист',
      user: 'Пользователь',
      global_ads_settings: 'Глобальные настройки рекламы'
    };
    return labels[entityType] || entityType;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Журнал действий</Typography>
        <IconButton onClick={() => { loadLogs(); loadCount(); }}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Фильтры */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Пользователь</InputLabel>
              <Select
                value={filters.user_id}
                onChange={(e) => handleFilterChange('user_id', e.target.value)}
                label="Пользователь"
              >
                <MenuItem value="">Все</MenuItem>
                {availableFilters.users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Тип сущности</InputLabel>
              <Select
                value={filters.entity_type}
                onChange={(e) => handleFilterChange('entity_type', e.target.value)}
                label="Тип сущности"
              >
                <MenuItem value="">Все</MenuItem>
                {availableFilters.entity_types.map((type) => (
                  <MenuItem key={type} value={type}>
                    {getEntityTypeLabel(type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Действие</InputLabel>
              <Select
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
                label="Действие"
              >
                <MenuItem value="">Все</MenuItem>
                {availableFilters.actions.map((action) => (
                  <MenuItem key={action} value={action}>
                    {getActionLabel(action)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Таблица логов */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Дата и время</TableCell>
              <TableCell>Пользователь</TableCell>
              <TableCell>Действие</TableCell>
              <TableCell>Тип сущности</TableCell>
              <TableCell>ID сущности</TableCell>
              <TableCell>IP адрес</TableCell>
              <TableCell>Детали</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log.id}>
                <TableCell>{formatDate(log.created_at)}</TableCell>
                <TableCell>{log.user?.username || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={getActionLabel(log.action)}
                    color={getActionColor(log.action)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{getEntityTypeLabel(log.entity_type) || '-'}</TableCell>
                <TableCell>{log.entity_id || '-'}</TableCell>
                <TableCell>{log.ip_address || '-'}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleViewDetails(log)}>
                    <VisibilityIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="Строк на странице:"
        />
      </TableContainer>

      {/* Диалог деталей */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Детали действия</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Дата:</strong> {formatDate(selectedLog.created_at)}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Пользователь:</strong> {selectedLog.user?.username}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Действие:</strong> {getActionLabel(selectedLog.action)}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Тип:</strong> {getEntityTypeLabel(selectedLog.entity_type)}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                <strong>IP:</strong> {selectedLog.ip_address}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                <strong>User Agent:</strong> {selectedLog.user_agent}
              </Typography>
              {selectedLog.details && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    <strong>Детали изменений:</strong>
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: '#f5f5f5', maxHeight: 400, overflow: 'auto' }}>
                    <pre style={{ margin: 0, fontSize: '12px' }}>
                      {JSON.stringify(selectedLog.details, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminLogsPage;
